import { useEffect, useRef, useState } from 'react';
import { getPrediction, confidenceBand, type Prediction } from '../services/api';

const FETCH_TIMEOUT_MS = 2000;

interface AIPredictionPanelProps {
  eventId: string;
  homeTeam: string;
  awayTeam: string;
}

function ConfidenceBadge({ confidence }: { confidence: number }) {
  const band = confidenceBand(confidence);
  const map = {
    high: { label: 'High confidence', classes: 'bg-primary/10 text-primary' },
    medium: { label: 'Medium confidence', classes: 'bg-cta/10 text-cta' },
    low: { label: 'Low confidence', classes: 'bg-surface-2 text-text-muted' },
  } as const;
  const { label, classes } = map[band];
  return (
    <span className={`rounded-pill px-2 py-0.5 text-xs font-semibold ${classes}`}>
      {label}
    </span>
  );
}

function ProbBar({ label, prob, color }: { label: string; prob: number; color: string }) {
  const raw = Math.round((prob ?? 0) * 100);
  const pct = Number.isFinite(raw) ? Math.max(0, Math.min(100, raw)) : 0;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-text-secondary">{label}</span>
        <span className="font-mono font-semibold text-text-primary">{pct}%</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-bar bg-surface-2">
        <div className={`h-full rounded-bar transition-all duration-700 ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/** One-line, plain-English summary of what the AI is saying. */
function takeaway(p: Prediction, home: string, away: string): string {
  const opts = [
    { who: home, prob: p.home_prob, kind: 'win' as const },
    { who: 'a draw', prob: p.draw_prob, kind: 'draw' as const },
    { who: away, prob: p.away_prob, kind: 'win' as const },
  ];
  const top = opts.reduce((a, b) => (b.prob > a.prob ? b : a));
  const pct = Math.round((top.prob ?? 0) * 100);
  const band = confidenceBand(p.confidence);

  if (top.kind === 'draw') {
    return `Our AI sees this as tight — a draw is the single most likely result (${pct}%).`;
  }
  if (band === 'high') return `Our AI makes ${top.who} strong favourites to win (${pct}% chance).`;
  if (band === 'medium') return `Our AI gives ${top.who} the edge to win (${pct}% chance) — but it's not a sure thing.`;
  return `It's wide open — ${top.who} are slight favourites (${pct}%), but any result is possible.`;
}

export default function AIPredictionPanel({ eventId, homeTeam, awayTeam }: AIPredictionPanelProps) {
  const [prediction, setPrediction] = useState<Prediction | null>(null);
  const [loading, setLoading] = useState(true);
  const [hidden, setHidden] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    setLoading(true);
    setPrediction(null);
    setHidden(false);
    const abort = new AbortController();
    abortRef.current = abort;
    const timeoutId = setTimeout(() => abort.abort(), FETCH_TIMEOUT_MS);

    getPrediction(eventId)
      .then((res) => {
        if (!abort.signal.aborted) setPrediction(res.data);
      })
      .catch(() => {
        if (!abort.signal.aborted) setHidden(true);
      })
      .finally(() => {
        clearTimeout(timeoutId);
        if (!abort.signal.aborted) setLoading(false);
      });

    return () => {
      clearTimeout(timeoutId);
      abort.abort();
    };
  }, [eventId]);

  if (hidden) return null;

  if (loading) {
    return (
      <div className="rounded-card border border-border bg-surface p-5 shadow-card space-y-3" aria-busy="true">
        <div className="shimmer h-5 w-48 rounded" />
        <div className="shimmer h-4 w-full rounded" />
        <div className="shimmer h-3 w-4/5 rounded" />
        <div className="shimmer h-3 w-3/4 rounded" />
      </div>
    );
  }

  if (!prediction) return null;

  return (
    <div className="rounded-card border border-primary/20 bg-primary/5 p-5 shadow-card">
      {/* Header */}
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="font-heading text-base font-bold text-text-primary">🤖 AI Match Prediction</h3>
        <ConfidenceBadge confidence={prediction.confidence} />
      </div>

      {/* Plain-language takeaway — the headline a newcomer reads first */}
      <p className="mb-4 rounded-lg bg-white/70 px-3 py-2 text-sm font-medium leading-relaxed text-text-primary">
        {takeaway(prediction, homeTeam, awayTeam)}
      </p>

      {/* Probability bars, labelled with the real team names */}
      <div className="mb-3 space-y-3">
        <ProbBar label={`${homeTeam} win`} prob={prediction.home_prob} color="bg-primary" />
        <ProbBar label="Draw" prob={prediction.draw_prob} color="bg-text-muted" />
        <ProbBar label={`${awayTeam} win`} prob={prediction.away_prob} color="bg-brand-pink" />
      </div>

      {prediction.explanation && (
        <p className="mb-3 text-sm text-text-secondary leading-relaxed">{prediction.explanation}</p>
      )}

      <p className="text-xs text-text-muted">
        These chances are based on live market odds. For information only — not betting advice. Please gamble responsibly.
      </p>
    </div>
  );
}
